import React from 'react'
import {
  loginClientDashboard,
  loginPartnerDashboard,
  syncJHRJobs,
  testQuestionnaireFilter,
  ValidateCandidateQuestionFilter,
  CandidateQuestionFilter
} from '@assembly/api/debug'
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Divider,
  Alert,
  Grid,
  CircularProgress
} from '@mui/material'
import isEmail from 'validator/lib/isEmail'
import { styled } from '@mui/material/styles'
import { useSnackbars } from '@assembly/hooks'
import { SnackbarType } from '@assembly/contexts'
import { getApiErrorMsg } from '@assembly/utils'
import Editor from '@monaco-editor/react'

const ButtonsBox = styled(Box)(() => ({
  display: 'flex',
  gap: '10px',
  marginTop: '15px',
}))

const EmailInput = styled(TextField)(({ theme }) => ({
  margin: 0,
  width: 500,
  [theme.breakpoints.down('md')]: {
    width: '100%',
  },
}))

const SectionPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
}))

export default function Debug() {
  const [email, setEmail] = React.useState<string>('')
  const [invalid_email, setInvalid_email] = React.useState<boolean>(false)
  const { addAlert } = useSnackbars()

  // JHR Sync state
  const [syncResponse, setSyncResponse] = React.useState<string>('')
  const [isSyncing, setIsSyncing] = React.useState<boolean>(false)

  // Filter test state
  const [filterJson, setFilterJson] = React.useState<string>(JSON.stringify({
    question: "Example question?",
    matchType: "EXACT",
    match: "example match"
  }, null, 2))
  const [answer, setAnswer] = React.useState<string>('')
  const [testResult, setTestResult] = React.useState<boolean | null>(null)
  const [isTestLoading, setIsTestLoading] = React.useState<boolean>(false)
  const [filterJsonError, setFilterJsonError] = React.useState<boolean>(false)

  const loginClientDashboardAsync = async () => {
    if (email.length === 0 || !isEmail(email)) {
      setInvalid_email(true)
      return
    }

    try {
      const params = new URLSearchParams()
      params.append('email', email)
      const { data } = await loginClientDashboard(params)
      window.location.href = data.message
    } catch (error: any) {
      addAlert({
        message: getApiErrorMsg(error),
        type: SnackbarType.Error,
      })
    }
  }

  const loginPartnerDashboardAsync = async () => {
    if (email.length === 0 || !isEmail(email)) {
      setInvalid_email(true)
      return
    }

    try {
      const params = new URLSearchParams()
      params.append('email', email)
      const { data } = await loginPartnerDashboard(params)
      window.location.href = data.message
    } catch (error) {
      addAlert({
        message: getApiErrorMsg(error),
        type: SnackbarType.Error,
      })
    }
  }

  const handleChangeEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value)
    setInvalid_email(false)
  }

  const handleSyncJHRJobs = async () => {
    setIsSyncing(true)
    setSyncResponse('')
    try {
      const { data } = await syncJHRJobs()
      if (data && data.message) {
        setSyncResponse(data.message)
      } else {
        setSyncResponse('Sync completed successfully')
      }
    } catch (error) {
      setSyncResponse(`Error: ${getApiErrorMsg(error)}`)
      addAlert({
        message: getApiErrorMsg(error),
        type: SnackbarType.Error,
      })
    } finally {
      setIsSyncing(false)
    }
  }

  const handleFilterEditorChange = (value: string | undefined) => {
    if (value !== undefined) {
      setFilterJson(value)
      try {
        JSON.parse(value)
        setFilterJsonError(false)
      } catch (e) {
        setFilterJsonError(true)
      }
    }
  }

  const handleAnswerChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAnswer(event.target.value)
  }

  const handleTestFilter = async () => {
    try {
      const filter = JSON.parse(filterJson) as CandidateQuestionFilter
      setIsTestLoading(true)
      setTestResult(null)

      const payload: ValidateCandidateQuestionFilter = {
        filter,
        answer
      }

      const { data } = await testQuestionnaireFilter(payload)
      if (data && data.result !== undefined) {
        setTestResult(data.result)
      }
    } catch (error) {
      addAlert({
        message: getApiErrorMsg(error),
        type: SnackbarType.Error,
      })
    } finally {
      setIsTestLoading(false)
    }
  }

  return (
    <Box>
      <SectionPaper>
        <Typography variant="h6" gutterBottom>Login Dashboards</Typography>
        <Divider sx={{ mb: 2 }} />
        <EmailInput
          value={email}
          margin="dense"
          label="Email"
          type="email"
          fullWidth
          variant="outlined"
          error={invalid_email}
          onChange={handleChangeEmail}
          helperText={invalid_email ? 'Required' : ''}
          sx={{ margin: 0, width: 500 }}
        />
        <ButtonsBox>
          <Button variant="contained" onClick={loginClientDashboardAsync}>
            Login Client Dashboard
          </Button>
          <Button variant="contained" onClick={loginPartnerDashboardAsync}>
            Login Partner Dashboard
          </Button>
        </ButtonsBox>
      </SectionPaper>

      <SectionPaper>
        <Typography variant="h6" gutterBottom>JHR Job Sync</Typography>
        <Divider sx={{ mb: 2 }} />
        <Button
          variant="contained"
          onClick={handleSyncJHRJobs}
          disabled={isSyncing}
          startIcon={isSyncing ? <CircularProgress size={20} /> : null}
        >
          {isSyncing ? 'Syncing...' : 'Sync JHR Jobs'}
        </Button>

        {syncResponse && (
          <Box mt={2}>
            <Typography variant="subtitle1" gutterBottom>Sync Results:</Typography>
            <Paper
              variant="outlined"
              sx={{ p: 2, backgroundColor: '#f5f5f5', whiteSpace: 'pre-line' }}
            >
              {syncResponse}
            </Paper>
          </Box>
        )}
      </SectionPaper>

      <SectionPaper>
        <Typography variant="h6" gutterBottom>Test Questionnaire Filter</Typography>
        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" gutterBottom>Filter (JSON)</Typography>
            <Box sx={{ border: '1px solid #e0e0e0', borderRadius: '4px', mb: 2 }}>
              <Editor
                height="200px"
                defaultLanguage="json"
                value={filterJson}
                onChange={handleFilterEditorChange}
                options={{
                  minimap: { enabled: false },
                  scrollBeyondLastLine: false,
                  fontSize: 14,
                  tabSize: 2,
                  automaticLayout: true,
                }}
              />
            </Box>
            {filterJsonError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Invalid JSON format
              </Alert>
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" gutterBottom>Answer to Test</Typography>
            <TextField
              fullWidth
              label="Candidate Answer"
              value={answer}
              onChange={handleAnswerChange}
              multiline
              rows={4}
              variant="outlined"
              sx={{ mb: 2 }}
            />
          </Grid>
        </Grid>

        <Button
          variant="contained"
          onClick={handleTestFilter}
          disabled={isTestLoading || filterJsonError}
          startIcon={isTestLoading ? <CircularProgress size={20} /> : null}
          sx={{ mt: 2 }}
        >
          {isTestLoading ? 'Testing...' : 'Test Filter'}
        </Button>

        {testResult !== null && (
          <Box mt={2}>
            <Alert severity={testResult ? "success" : "warning"}>
              Filter {testResult ? "matches" : "does not match"} the provided answer.
            </Alert>
          </Box>
        )}
      </SectionPaper>
    </Box>
  )
}
